// First-run setup (owner request 14 Oct 2026): a guided wizard for a new advisor — profile & ICCS registration
// with the licence classes (life / general), office & availability, importing clients, connecting calendar / e-mail,
// the avrastro.com booking link, which tools to show, backups & security — and a checklist that stays on Today until
// every step is done or skipped (progress %, skip, resume, hide for a week).
//
// Everything lives in settings (no schema change): `practice`, `office`, `booking.url`, `modules.hidden`, `setup`.
// A step counts as done when the data shows it (e.g. a registration number and a licence class are saved) or when the
// advisor marks it done; a skipped step can be resumed at any time. Nothing here ever deletes data.
import { DomainError, opt, type Ctx } from './cmd.ts';
import type { Changeset, WorkspaceView } from './changeset.ts';
import { getSetting, saveSetting } from './services.ts';
import { addDays, type Day } from './dates.ts';

export const SETUP_STEPS = ['profile', 'office', 'clients', 'connect', 'bookings', 'modules', 'safety'] as const;
export type SetupStep = typeof SETUP_STEPS[number];
export type SetupMark = 'done' | 'skipped';
export interface SetupState { steps: Partial<Record<SetupStep, SetupMark>>; startedAt?: string | null; snoozedUntil?: Day | null; completedAt?: string | null }

/** How the advisor is registered with the ICCS (Insurance Companies Control Service), as written in the register. */
export const PRACTICE_CAPACITIES = ['tied_agent', 'agent', 'broker', 'advisor_rep', 'ancillary'] as const;
export type PracticeCapacity = typeof PRACTICE_CAPACITIES[number];
export interface Practice {
  /** licence classes of the registration: life and/or general insurance */
  lines: { life: boolean; general: boolean };
  capacity: PracticeCapacity | null;
  /** ICCS registration number */
  regNo: string | null;
  /** insurers the advisor works with (as the advisor names them) */
  insurers: string[];
}
export const DEFAULT_PRACTICE: Practice = { lines: { life: true, general: true }, capacity: null, regNo: null, insurers: [] };

export const MEETING_MODES = ['office', 'online', 'phone', 'client_place', 'cafe'] as const;
export type MeetingMode = typeof MEETING_MODES[number];
export interface OfficeHours {
  /** ISO weekdays 1 (Monday) … 7 (Sunday) */
  days: number[];
  from: string; to: string;
  meetingMinutes: number;
  modes: MeetingMode[];
  address: string | null; mapsUrl: string | null; phone: string | null;
}
export const DEFAULT_OFFICE: OfficeHours = { days: [1, 2, 3, 4, 5], from: '09:00', to: '17:00', meetingMinutes: 45, modes: ['office', 'online', 'phone'], address: null, mapsUrl: null, phone: null };

// --- readers ------------------------------------------------------------------------------------------------------
export function getPractice(v: WorkspaceView): Practice {
  const raw = getSetting<Partial<Practice> | null>(v, 'practice', null);
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_PRACTICE, lines: { ...DEFAULT_PRACTICE.lines } };
  const lines = raw.lines && typeof raw.lines === 'object' ? { life: raw.lines.life !== false, general: raw.lines.general !== false } : { ...DEFAULT_PRACTICE.lines };
  return {
    lines,
    capacity: (PRACTICE_CAPACITIES as readonly string[]).includes(String(raw.capacity)) ? raw.capacity as PracticeCapacity : null,
    regNo: opt(raw.regNo),
    insurers: Array.isArray(raw.insurers) ? raw.insurers.map((x) => String(x).trim()).filter(Boolean) : [],
  };
}
/** general-insurance tools (insured items, the general renewal board) are shown unless the advisor is registered for life only */
export const generalToolsOn = (v: WorkspaceView): boolean => getPractice(v).lines.general;
export const lifeToolsOn = (v: WorkspaceView): boolean => getPractice(v).lines.life;

export function getOffice(v: WorkspaceView): OfficeHours {
  const raw = getSetting<Partial<OfficeHours> | null>(v, 'office', null) ?? {};
  const days = Array.isArray(raw.days) ? [...new Set(raw.days.map(Number).filter((d) => Number.isInteger(d) && d >= 1 && d <= 7))].sort() : DEFAULT_OFFICE.days;
  const modes = Array.isArray(raw.modes) ? raw.modes.filter((m): m is MeetingMode => (MEETING_MODES as readonly string[]).includes(m)) : DEFAULT_OFFICE.modes;
  return {
    days, modes,
    from: TIME.test(String(raw.from)) ? String(raw.from) : DEFAULT_OFFICE.from,
    to: TIME.test(String(raw.to)) ? String(raw.to) : DEFAULT_OFFICE.to,
    meetingMinutes: Number.isInteger(raw.meetingMinutes) && raw.meetingMinutes! >= 10 && raw.meetingMinutes! <= 240 ? raw.meetingMinutes! : DEFAULT_OFFICE.meetingMinutes,
    address: opt(raw.address), mapsUrl: opt(raw.mapsUrl), phone: opt(raw.phone),
  };
}
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** Module ids the advisor may hide (core screens — Now, People, Settings, Data & safety, Account, Help — always stay). */
export const ALWAYS_SHOWN_MODULES: readonly string[] = ['now', 'people', 'settings', 'data', 'account', 'help', 'lessons', 'setup'];
export function hiddenModules(v: WorkspaceView): string[] {
  const raw = getSetting<unknown>(v, 'modules.hidden', []);
  return Array.isArray(raw) ? raw.map(String).filter((id) => !ALWAYS_SHOWN_MODULES.includes(id)) : [];
}

export const bookingUrl = (v: WorkspaceView): string | null => opt(getSetting<string | null>(v, 'booking.url', null));
export function getSetupState(v: WorkspaceView): SetupState {
  const raw = getSetting<Partial<SetupState> | null>(v, 'setup', null) ?? {};
  const steps: SetupState['steps'] = {};
  for (const s of SETUP_STEPS) { const m = (raw.steps as Record<string, unknown> | undefined)?.[s]; if (m === 'done' || m === 'skipped') steps[s] = m; }
  return { steps, startedAt: raw.startedAt ?? null, snoozedUntil: raw.snoozedUntil ?? null, completedAt: raw.completedAt ?? null };
}

// --- the checklist ------------------------------------------------------------------------------------------------
/** facts the domain cannot see by itself (device storage, connected accounts) — passed in by the app */
export interface SetupSignals { backupAgeDays?: number | null; encrypted?: boolean; cloudBackup?: boolean; outlookConnected?: boolean }
export interface SetupItem { id: SetupStep; status: 'done' | 'skipped' | 'todo'; /** true when the data itself shows the step is done */ auto: boolean }
export interface SetupProgress { items: SetupItem[]; done: number; skipped: number; total: number; percent: number; complete: boolean; snoozed: boolean; next: SetupStep | null }

/** what the data already shows (independent of what the advisor marked) */
export function setupAutoDone(v: WorkspaceView, s: SetupSignals = {}): Record<SetupStep, boolean> {
  const practice = getSetting<unknown>(v, 'practice', null);
  const p = getPractice(v);
  const adv = getSetting<Record<string, string> | null>(v, 'advisor', null) ?? {};
  const brand = v.rows('branding_profiles').find((b) => b.scope === 'advisor');
  const regNo = p.regNo ?? opt(brand?.licenceNumber) ?? opt(adv.license);
  const name = opt(brand?.advisorName) ?? opt(adv.name);
  const office = getSetting<unknown>(v, 'office', null);
  const backupOk = s.backupAgeDays != null && s.backupAgeDays <= 31;
  return {
    profile: !!practice && !!regNo && !!name && (p.lines.life || p.lines.general),
    office: !!office && (!!getOffice(v).address || getOffice(v).days.length > 0),
    clients: v.rows('persons').some((x) => !x.archivedAt && (x.stage === 'client' || x.stage === 'prospect')) && !getSetting<boolean>(v, 'demo', false),
    connect: !!s.outlookConnected,
    bookings: !!bookingUrl(v),
    modules: Array.isArray(getSetting<unknown>(v, 'modules.hidden', null)),
    safety: backupOk || !!s.encrypted || !!s.cloudBackup,
  };
}

export function setupProgress(v: WorkspaceView, today: Day, signals: SetupSignals = {}): SetupProgress {
  const st = getSetupState(v);
  const auto = setupAutoDone(v, signals);
  const items: SetupItem[] = SETUP_STEPS.map((id) => ({ id, auto: auto[id], status: auto[id] || st.steps[id] === 'done' ? 'done' : st.steps[id] === 'skipped' ? 'skipped' : 'todo' }));
  const done = items.filter((i) => i.status === 'done').length;
  const skipped = items.filter((i) => i.status === 'skipped').length;
  const total = items.length;
  return {
    items, done, skipped, total, percent: Math.round((done / total) * 100),
    complete: done + skipped === total,
    snoozed: !!st.snoozedUntil && st.snoozedUntil > today,
    next: items.find((i) => i.status === 'todo')?.id ?? null,
  };
}
/** the Today card shows while something is still to do and the advisor has not hidden it for now */
export const showSetupCard = (p: SetupProgress) => !p.complete && !p.snoozed;

// --- commands -----------------------------------------------------------------------------------------------------
export function markSetupStep(v: WorkspaceView, step: SetupStep, mark: SetupMark | null, ctx: Ctx): Changeset {
  if (!(SETUP_STEPS as readonly string[]).includes(step)) throw new DomainError('setup.step');
  const st = getSetupState(v);
  const steps = { ...st.steps };
  if (mark) steps[step] = mark; else delete steps[step];
  const all = SETUP_STEPS.every((s) => steps[s]);
  return saveSetting(v, 'setup', { ...st, steps, startedAt: st.startedAt ?? ctx.now, completedAt: all ? st.completedAt ?? ctx.now : null }, ctx);
}
/** "hide for now": the card comes back after `days` (default a week); every step stays as it was */
export function snoozeSetup(v: WorkspaceView, ctx: Ctx, days = 7): Changeset {
  const st = getSetupState(v);
  return saveSetting(v, 'setup', { ...st, startedAt: st.startedAt ?? ctx.now, snoozedUntil: addDays(ctx.today, Math.max(1, Math.min(60, Math.round(days)))) }, ctx);
}
export function resumeSetup(v: WorkspaceView, ctx: Ctx): Changeset {
  const st = getSetupState(v);
  return saveSetting(v, 'setup', { ...st, startedAt: st.startedAt ?? ctx.now, snoozedUntil: null }, ctx);
}

export interface PracticeInput { lines: { life: boolean; general: boolean }; capacity?: string | null; regNo?: string | null; insurers?: readonly string[] | string | null }
export function savePractice(v: WorkspaceView, input: PracticeInput, ctx: Ctx): Changeset {
  if (!input.lines || (!input.lines.life && !input.lines.general)) throw new DomainError('setup.lines_required');
  const capacity = input.capacity ? String(input.capacity) : null;
  if (capacity && !(PRACTICE_CAPACITIES as readonly string[]).includes(capacity)) throw new DomainError('setup.capacity');
  const regNo = opt(input.regNo);
  if (regNo && regNo.length > 40) throw new DomainError('setup.reg_no');
  const insurers = (typeof input.insurers === 'string' ? input.insurers.split(/[,;\n]+/) : [...(input.insurers ?? [])]).map((x) => String(x).trim()).filter(Boolean);
  const value: Practice = { lines: { life: !!input.lines.life, general: !!input.lines.general }, capacity: capacity as PracticeCapacity | null, regNo, insurers: [...new Set(insurers)].slice(0, 30) };
  return saveSetting(v, 'practice', value, ctx);
}

export interface OfficeInput { days?: readonly number[]; from?: string; to?: string; meetingMinutes?: number | null; modes?: readonly string[]; address?: string | null; mapsUrl?: string | null; phone?: string | null }
export function saveOffice(v: WorkspaceView, input: OfficeInput, ctx: Ctx): Changeset {
  const cur = getOffice(v);
  const from = input.from ?? cur.from, to = input.to ?? cur.to;
  if (!TIME.test(from) || !TIME.test(to)) throw new DomainError('setup.time');
  if (from >= to) throw new DomainError('setup.hours_order');
  const mapsUrl = input.mapsUrl !== undefined ? opt(input.mapsUrl) : cur.mapsUrl;
  if (mapsUrl && !/^https:\/\//i.test(mapsUrl)) throw new DomainError('setup.https');
  const minutes = input.meetingMinutes ?? cur.meetingMinutes;
  if (!Number.isInteger(minutes) || minutes < 10 || minutes > 240) throw new DomainError('setup.minutes');
  const value: OfficeHours = {
    days: input.days ? [...new Set(input.days.map(Number).filter((d) => Number.isInteger(d) && d >= 1 && d <= 7))].sort() : cur.days,
    from, to, meetingMinutes: minutes,
    modes: input.modes ? input.modes.filter((m): m is MeetingMode => (MEETING_MODES as readonly string[]).includes(m)) : cur.modes,
    address: input.address !== undefined ? opt(input.address) : cur.address, mapsUrl, phone: input.phone !== undefined ? opt(input.phone) : cur.phone,
  };
  return saveSetting(v, 'office', value, ctx);
}

/** the advisor's personal booking page (from avrastro.com → Advisor Hub → Availability); https only */
export function saveBookingUrl(v: WorkspaceView, url: string | null, ctx: Ctx): Changeset {
  const u = opt(url);
  if (u) { let ok = false; try { ok = new URL(u).protocol === 'https:'; } catch { ok = false; } if (!ok) throw new DomainError('setup.https'); }
  return saveSetting(v, 'booking.url', u, ctx);
}

export function saveHiddenModules(v: WorkspaceView, ids: readonly string[], ctx: Ctx): Changeset {
  const clean = [...new Set(ids.map(String).filter((id) => /^[a-z][a-z0-9_-]{1,30}$/.test(id) && !ALWAYS_SHOWN_MODULES.includes(id)))].sort();
  return saveSetting(v, 'modules.hidden', clean, ctx);
}

/** Tools that only make sense for general insurance (hidden when the registration is life only). Ids are module ids
 *  or `focus:<tab>` / `renewals:general` sections inside a screen. */
export const GENERAL_ONLY_SECTIONS: readonly string[] = ['focus:insured', 'renewals:general'];
/** Tools that only make sense for life business (hidden when the registration is general only). */
export const LIFE_ONLY_SECTIONS: readonly string[] = ['focus:kyc', 'tax', 'protection'];
export function sectionShown(v: WorkspaceView, id: string): boolean {
  const p = getPractice(v);
  if (!p.lines.general && GENERAL_ONLY_SECTIONS.includes(id)) return false;
  if (!p.lines.life && LIFE_ONLY_SECTIONS.includes(id)) return false;
  return !hiddenModules(v).includes(id);
}

// --- training lessons: progress per lesson (setting `lessons.progress`, synced like every setting) -------------------
export interface LessonMark { doneAt?: string | null; /** correct answers of the last quiz attempt */ quiz?: number | null; of?: number | null; seenAt?: string | null }
export function lessonProgress(v: WorkspaceView): Record<string, LessonMark> {
  const raw = getSetting<Record<string, LessonMark> | null>(v, 'lessons.progress', null);
  return raw && typeof raw === 'object' ? raw : {};
}
export function saveLessonProgress(v: WorkspaceView, slug: string, patch: { done?: boolean; seen?: boolean; quiz?: { correct: number; of: number } }, ctx: Ctx): Changeset {
  if (!/^[a-z0-9-]{2,60}$/.test(slug)) throw new DomainError('lesson.slug');
  const all = { ...lessonProgress(v) };
  const cur: LessonMark = { ...(all[slug] ?? {}) };
  if (patch.seen && !cur.seenAt) cur.seenAt = ctx.now;
  if (patch.done === true) cur.doneAt = cur.doneAt ?? ctx.now;
  if (patch.done === false) cur.doneAt = null;
  if (patch.quiz) {
    const { correct, of } = patch.quiz;
    if (!Number.isInteger(of) || of < 1 || !Number.isInteger(correct) || correct < 0 || correct > of) throw new DomainError('lesson.quiz');
    cur.quiz = correct; cur.of = of;
  }
  all[slug] = cur;
  return saveSetting(v, 'lessons.progress', all, ctx);
}
